import { keyBy } from "lodash";
import groupBy from "lodash/groupBy";
import intersection from "lodash/intersection";
import partition from "lodash/partition";
import { all, put, select, takeEvery } from "redux-saga/effects";
import { v4 } from "uuid";

import {
  MasterRuleAction,
  MasterRuleBookAction,
  RuleAction,
  RuleBookAction,
} from "actions";

import { RuleSetAction } from "actions/ruleSets";

import {
  MASTER_RULE,
  MASTER_RULE_BOOK,
  RULE,
  RULE_BOOK,
} from "constants/actionTypes";

import { isManagedRuleOutOfSync } from "lib/rules";

import {
  getLastOrderByMasterRuleBookId,
  getLastOrderByRuleBookId,
  getMasterRuleBookById,
  getMasterRuleById,
  getMasterRuleIdsByMasterRuleBookId,
  getMasterRules,
  getMasterRulesByMasterRuleBookId,
  getMasterRuleSets,
  getRuleBookById,
  getRuleById,
  getRuleIdsByMasterRuleId,
  getRuleIdsByRuleBookId,
  getRules,
  getRulesByRuleBookId,
  selectRuleIdsByMasterRuleIdLookup,
  selectSetRuleSetIdByMasterRuleSetIdLookup,
} from "selectors";

import {
  getMasterRuleSetById,
  getMasterRuleSetsByMasterRuleBookId,
  getRuleSetsByRuleBookId,
} from "selectors/ruleSets";

function* onSubscribeToMasterRuleBook(action) {
  const { ruleBookId, masterRuleBookId, options = null } = action;

  const state = yield select();
  const masterRules =
    getMasterRulesByMasterRuleBookId(masterRuleBookId)(state) || [];
  const masterRuleBook = getMasterRuleBookById(masterRuleBookId)(state);

  const lastOrder = getLastOrderByRuleBookId(ruleBookId)(state);
  const ruleSetIdByMasterRuleSetId =
    selectSetRuleSetIdByMasterRuleSetIdLookup(state);

  const handledRules = masterRules.filter(masterRule => !masterRule.is_deleted);

  const collectedRules = [];

  const handledMasterRuleSetIds = [];

  let handledRuleSetId = null;

  for (const [index, masterRule] of handledRules.entries()) {
    const existingRuleIds = intersection(
      getRuleIdsByMasterRuleId(masterRule.id)(state),
      getRuleIdsByRuleBookId(ruleBookId),
    );
    const masterRuleSet = getMasterRuleSetById(masterRule.rule_set_id)(state);

    const ruleSetId =
      ruleSetIdByMasterRuleSetId[masterRule.rule_set_id] || null;

    if (masterRuleSet) {
      if (!ruleSetId && !handledMasterRuleSetIds.includes(masterRuleSet.id)) {
        handledRuleSetId = v4();
        yield put(
          RuleSetAction.create(
            {
              ...masterRuleSet,
              id: handledRuleSetId,
              master_rule_set_id: masterRule.rule_set_id,
              rule_book_id: ruleBookId,
            },
            { changeReason: `Subscribed to "${masterRuleBook.name}"` },
          ),
        );
      } else if (ruleSetId) {
        handledRuleSetId = ruleSetId;
        yield put(
          RuleSetAction.update(
            {
              ...masterRuleSet,
              id: handledRuleSetId,
              master_rule_set_id: masterRule.rule_set_id,
              rule_book_id: ruleBookId,
            },
            { changeReason: `Subscribed to "${masterRuleBook.name}"` },
          ),
        );
      }
      handledMasterRuleSetIds.push(masterRuleSet.id);
    }

    collectedRules.push({
      ...masterRule,
      id: existingRuleIds[0]?.id || v4(),
      comment: "",
      master_rule_id: masterRule.id,
      order: lastOrder + index + 1,
      rule_book_id: ruleBookId,
      input_field: masterRuleSet ? null : masterRule.input_field,
      rule_set_id: handledRuleSetId,
    });
  }

  // For this Rule Book add a Managed Rule for each of the Master Rules in the given Master Rule Book
  yield all(
    collectedRules.map(rule =>
      put(
        RuleAction.create(rule, {
          changeReason: `Subscribed to "${masterRuleBook.name}"`,
          ...options,
        }),
      ),
    ),
  );
}

function* onUnsubscribeFromMasterRuleBook(action) {
  const { ruleBookId, masterRuleBookId, options = null } = action;

  const state = yield select();
  const masterRuleBook = getMasterRuleBookById(masterRuleBookId)(state);
  const masterRuleIds =
    getMasterRuleIdsByMasterRuleBookId(masterRuleBookId)(state) || [];

  const rules = getRulesByRuleBookId(ruleBookId)(state) || [];

  // For this Rule Book remove all Managed Rules which are related to the Master Rules in the given Master Rule Book
  yield all(
    rules
      .filter(rule => masterRuleIds.includes(rule.master_rule_id))
      .map(rule =>
        put(
          RuleAction.delete(rule.id, {
            changeReason: `Unsubscribed from "${masterRuleBook.name}"`,
            ...options,
          }),
        ),
      ),
  );
}

function* onCopyRule(action) {
  const { ruleId, options = null } = action;
  const state = yield select();
  const rule = getRuleById(ruleId)(state);

  const lastOrder = getLastOrderByRuleBookId(rule.rule_book_id)(state);

  yield put(
    RuleAction.create(
      { ...rule, id: v4(), name: `${rule.name} (Copy)`, order: lastOrder + 1 },
      { changeReason: `Copied from "${rule.name}"`, ...options },
    ),
  );
}

function* onCopyMasterRule(action) {
  const { masterRuleId, options = null } = action;
  const state = yield select();
  const masterRule = getMasterRuleById(masterRuleId)(state);

  const lastOrder = getLastOrderByMasterRuleBookId(masterRule.rule_book_id)(
    state,
  );

  yield put(
    MasterRuleAction.create(
      {
        ...masterRule,
        id: v4(),
        name: `${masterRule.name} (Copy)`,
        order: lastOrder + 1,
      },
      { changeReason: `Copied from "${masterRule.name}"`, ...options },
    ),
  );
}

function decrementRuleOrMasterRuleOrder(thisRule, prevRule) {
  return [
    { ...thisRule, order: prevRule.order },
    {
      ...prevRule,
      order: prevRule.order + 1,
    },
  ];
}

function incrementRuleOrMasterRuleOrder(thisRule, nextRule) {
  return [
    { ...thisRule, order: nextRule.order },
    {
      ...nextRule,
      order: nextRule.order - 1,
    },
  ];
}

function* onMoveUpRule(action) {
  const { ruleId, options = null } = action;
  const state = yield select();
  const ruleBookId = getRuleById(ruleId)(state).rule_book_id;
  const rules = getRulesByRuleBookId(ruleBookId)(state);

  const thisIndex = rules.findIndex(rule => rule.id === ruleId);
  const thisRule = rules[thisIndex];
  const prevRule = rules[thisIndex - 1];

  yield all(
    decrementRuleOrMasterRuleOrder(thisRule, prevRule).map(rule =>
      put(
        RuleAction.update(
          { id: rule.id, order: rule.order },
          {
            changeReason: "Updated order",
            ...options,
          },
        ),
      ),
    ),
  );
}

function* onMoveDownRule(action) {
  const { ruleId, options = null } = action;
  const state = yield select();
  const ruleBookId = getRuleById(ruleId)(state).rule_book_id;
  const rules = getRulesByRuleBookId(ruleBookId)(state);

  const thisIndex = rules.findIndex(rule => rule.id === ruleId);
  const thisRule = rules[thisIndex];
  const nextRule = rules[thisIndex + 1];

  yield all(
    incrementRuleOrMasterRuleOrder(thisRule, nextRule).map(rule =>
      put(
        RuleAction.update(
          { id: rule.id, order: rule.order },
          {
            changeReason: "Updated order",
            ...options,
          },
        ),
      ),
    ),
  );
}

function* onMoveUpMasterRule(action) {
  const { masterRuleId, options = null } = action;
  const state = yield select();
  const masterRuleBookId = getMasterRuleById(masterRuleId)(state).rule_book_id;
  const masterRules = getMasterRulesByMasterRuleBookId(masterRuleBookId)(state);

  const currentIndex = masterRules.findIndex(
    masterRule => masterRule.id === masterRuleId,
  );
  const currentMasterRule = masterRules[currentIndex];
  const prevMasterRule = masterRules[currentIndex - 1];

  yield all(
    decrementRuleOrMasterRuleOrder(currentMasterRule, prevMasterRule).map(
      updatedMasterRule =>
        put(
          MasterRuleAction.update(updatedMasterRule, {
            changeReason: "Updated order",
            ...options,
          }),
        ),
    ),
  );
}

function* onMoveDownMasterRule(action) {
  const { masterRuleId, options = null } = action;
  const state = yield select();
  const masterRuleBookId = getMasterRuleById(masterRuleId)(state).rule_book_id;
  const masterRules = getMasterRulesByMasterRuleBookId(masterRuleBookId)(state);

  const currentIndex = masterRules.findIndex(
    masterRule => masterRule.id === masterRuleId,
  );
  const currentMasterRule = masterRules[currentIndex];
  const nextMasterRule = masterRules[currentIndex + 1];

  yield all(
    incrementRuleOrMasterRuleOrder(currentMasterRule, nextMasterRule).map(
      updatedMasterRule =>
        put(
          MasterRuleAction.update(updatedMasterRule, {
            changeReason: "Updated order",
            ...options,
          }),
        ),
    ),
  );
}

/**
 * Generates a mapping of original item IDs to new UUIDs.
 *
 * @param {Array} items - The array of items, each with an `id` property.
 * @returns {Object} An object mapping each item's original `id` to a new UUID.
 */
function createIdReplacementMap(items) {
  return items.reduce((acc, item) => {
    acc[item.id] = v4();
    return acc;
  }, {});
}

/**
 * Prepares an Array of RuleSets for a copy operation of a new Rule Book.
 * Specifically operates on Rule that already exist in the source Rule Book
 */
function copyRuleSets(ruleBookRuleSets, rulesInRuleSets, ruleIdReplacementMap) {
  const ruleSetById = keyBy(ruleBookRuleSets, "id");
  const rulesByRuleSetId = groupBy(rulesInRuleSets, "rule_set_id");
  const ruleSetsCopy = [];

  Object.values(ruleSetById).forEach(ruleSet => {
    const rules = rulesByRuleSetId[ruleSet.id] || [];
    if (!rules.length) {
      return;
    }
    ruleSetsCopy.push({
      ...ruleSet,
      id: undefined,
      // Give it a distinct name if it doesn't yet have a name
      name: ruleSet.name ? ruleSet.name : `${rules[0].name} Rule Set`,
      rules: rules.map(rule => ({
        ...rule,
        rule_set_id: undefined,
        id: ruleIdReplacementMap[rule.id],
      })),
    });
  });

  return ruleSetsCopy;
}

/**
 * Prepares an Array of RuleSets for a copy operation of a new Rule Book.
 * Specifically operates on Rules that do not exist in a Rule Set
 */
function copyRulesWithoutRuleSets(rulesWithoutRuleSets, newIds) {
  const ruleSetsCopy = [];
  const rulesByInputField = groupBy(rulesWithoutRuleSets, "input_field");
  Object.values(rulesByInputField).forEach(rules => {
    ruleSetsCopy.push({
      input_field: rules[0].input_field,
      name: `${rules[0].name} Rule Set`,
      rules: rules.map(rule => ({
        ...rule,
        rule_set_id: undefined,
        input_field: null,
        id: newIds[rule.id],
      })),
    });
  });
  return ruleSetsCopy;
}

/**
 * Repalaces The prerequisite rule IDs in the copied RuleSets as part of a Copy Rule Book operation
 * @param ruleSetsCopy
 * @param idReplacementMap
 */
function rewritePrerequisiteIds(ruleSetsCopy, idReplacementMap) {
  Object.values(ruleSetsCopy).forEach(ruleSet => {
    ruleSet.rules.forEach(rule => {
      rule.rule_set_id = undefined;
      rule.copied_prerequisite_rule_ids = rule.prerequisite_rule_ids.map(
        ruleId => idReplacementMap[ruleId],
      );
      rule.prerequisite_rule_ids = undefined;
    });
  });
}

function copyRuleGeneratorFactory(
  sourceRuleBookIdKey,
  destinationRuleBookIdKey,
  ruleBookGetterSelector,
  ruleBookRulesGetterSelector,
  ruleSetGetterSelector,
  ruleBookOfflineActionCreator,
) {
  return function* onCopyRuleBookGenFn(action) {
    const {
      [sourceRuleBookIdKey]: sourceRuleBookId,
      [destinationRuleBookIdKey]: destinationRuleBookId = v4(),
      options = null,
    } = action;
    const state = yield select();
    const ruleBook = ruleBookGetterSelector(sourceRuleBookId)(state);

    const ruleBookRules =
      ruleBookRulesGetterSelector(sourceRuleBookId)(state) || [];

    const ruleBookRuleSets =
      ruleSetGetterSelector(sourceRuleBookId)(state) || [];

    // To maintain the link between pre-requisite rules, create a lookup.
    const ruleIdReplacementMap = createIdReplacementMap(ruleBookRules);
    const ruleSetIdReplacementMap = createIdReplacementMap(ruleBookRuleSets);

    const [rulesInRuleSets, rulesWithoutRuleSets] = partition(
      ruleBookRules,
      rule => Boolean(rule.rule_set_id),
    );

    const ruleSetsCopy = [
      // For rules that are in rule sets, we need to copy the rule set first
      ...copyRuleSets(
        ruleBookRuleSets,
        rulesInRuleSets,
        ruleSetIdReplacementMap,
      ),
      // Add the rules that are not in and existing rule set to a new RuleSet, grouped with other Rules sharing the same input field
      ...copyRulesWithoutRuleSets(rulesWithoutRuleSets, ruleIdReplacementMap),
    ];

    // Wire up the Rule Ids from the Prerequisite Rules to the new Rule Ids
    rewritePrerequisiteIds(ruleSetsCopy, ruleIdReplacementMap);

    yield put(
      ruleBookOfflineActionCreator.create(
        {
          ...ruleBook,
          id: destinationRuleBookId,
          name: `${ruleBook.name} (Copy)`,
          rule_sets: ruleSetsCopy,
        },
        { changeReason: `Copied from "${ruleBook.name}"`, ...options },
      ),
    );
  };
}

const onCopyMasterRuleBook = copyRuleGeneratorFactory(
  "sourceMasterRuleBookId",
  "destinationMasterRuleBookId",
  getMasterRuleBookById,
  getMasterRulesByMasterRuleBookId,
  getMasterRuleSetsByMasterRuleBookId,
  MasterRuleBookAction,
);
const onCopyRuleBook = copyRuleGeneratorFactory(
  "sourceRuleBookId",
  "destinationRuleBookId",
  getRuleBookById,
  getRulesByRuleBookId,
  getRuleSetsByRuleBookId,
  RuleBookAction,
);

function* onUpdateManagedRuleBook(action) {
  const { sourceMasterRuleBookId, managedRuleBookId, ruleId } = action;
  const state = yield select();
  const masterRuleIds = getMasterRuleIdsByMasterRuleBookId(
    sourceMasterRuleBookId,
  )(state);
  const ruleIdsByMasterRuleId = selectRuleIdsByMasterRuleIdLookup(state);
  const ruleSetIdByMasterRuleSetId =
    selectSetRuleSetIdByMasterRuleSetIdLookup(state);

  const rules = getRules(state);
  const masterRules = getMasterRules(state);
  const masterRuleSets = getMasterRuleSets(state);

  for (const masterRuleId of masterRuleIds) {
    const masterRule = masterRules[masterRuleId];
    const masterRuleSetId = masterRule.rule_set_id;
    const masterRuleSet = masterRuleSets[masterRuleSetId];

    let handledRuleSetId = null;

    const managedRuleSetId =
      ruleSetIdByMasterRuleSetId[masterRuleSetId] || null;

    // If the master rule has a rule set and a managed rule set doesn’t exist - make one
    // Otherwise update the existing rule set to match the master rule set.
    if (masterRuleSetId) {
      if (!managedRuleSetId) {
        handledRuleSetId = v4();
        yield put(
          RuleSetAction.create(
            {
              ...masterRuleSet,
              id: handledRuleSetId,
              master_rule_set_id: masterRuleSetId,
              rule_book_id: managedRuleBookId,
            },
            { changeReason: `Rolled down from "${masterRule.name}"` },
          ),
        );
      } else {
        handledRuleSetId = managedRuleSetId;
        yield put(
          RuleSetAction.update(
            {
              ...masterRuleSet,
              id: handledRuleSetId,
              master_rule_set_id: masterRuleSetId,
              rule_book_id: managedRuleBookId,
            },
            { changeReason: `Rolled down from "${masterRule.name}"` },
          ),
        );
      }
    }
    // If there are no rules for the master rule yet, then we need to make one
    if (!ruleIdsByMasterRuleId[masterRuleId] && !ruleId) {
      yield put(
        RuleAction.create(
          {
            ...masterRule,
            id: v4(),
            comment: "",
            master_rule_id: masterRule.id,
            rule_book_id: managedRuleBookId,
            rule_set_id: handledRuleSetId,
          },
          { changeReason: `Rolled down from "${masterRule.name}"` },
        ),
      );
      // eslint-disable-next-line no-continue
      continue; // Skip the rest of this iteration
    }
    const filteredManagedRuleIds =
      ruleIdsByMasterRuleId[masterRuleId]?.filter(
        managedRuleId => ruleId && ruleId === managedRuleId,
      ) || [];
    // Otherwise we can check the content and is_deleted of the master rule and managed rule
    // to see if it needs updating
    for (const managedRuleId of filteredManagedRuleIds) {
      const managedRule = rules[managedRuleId];
      if (masterRule.is_deleted) {
        yield put(RuleAction.delete(managedRule.id));
        // eslint-disable-next-line no-continue
        continue; // Skip the rest of this iteration
      } else if (isManagedRuleOutOfSync(managedRule, masterRule)) {
        yield put(
          RuleAction.update(
            {
              content: masterRule.content,
              criteria: masterRule.criteria,
              id: managedRule.id,
              input_field: masterRule.input_field,
              quantity_output_format: masterRule.quantity_output_format,
              quantity_raw_format: masterRule.quantity_raw_format,
              unit_amount_output_format: masterRule.unit_amount_output_format,
              unit_amount_raw_format: masterRule.unit_amount_raw_format,
              rule_set_id: handledRuleSetId,
            },
            {
              changeReason: "Updated from master rulebook (via the frontend)",
            },
          ),
        );
      }
    }
  }
}

export default function* rootSaga() {
  yield takeEvery(
    RULE_BOOK.UNSUBSCRIBE_FROM_MASTER_RULE_BOOK.ACTION,
    onUnsubscribeFromMasterRuleBook,
  );
  yield takeEvery(
    RULE_BOOK.SUBSCRIBE_TO_MASTER_RULE_BOOK.ACTION,
    onSubscribeToMasterRuleBook,
  );
  yield takeEvery(
    MASTER_RULE_BOOK.UPDATE_MANAGED_RULE_BOOK.ACTION,
    onUpdateManagedRuleBook,
  );

  yield takeEvery(RULE.COPY.ACTION, onCopyRule);
  yield takeEvery(RULE.MOVE_UP.ACTION, onMoveUpRule);
  yield takeEvery(RULE.MOVE_DOWN.ACTION, onMoveDownRule);

  yield takeEvery(MASTER_RULE_BOOK.COPY.ACTION, onCopyMasterRuleBook);

  yield takeEvery(RULE_BOOK.COPY.ACTION, onCopyRuleBook);

  yield takeEvery(MASTER_RULE.COPY.ACTION, onCopyMasterRule);
  yield takeEvery(MASTER_RULE.MOVE_UP.ACTION, onMoveUpMasterRule);
  yield takeEvery(MASTER_RULE.MOVE_DOWN.ACTION, onMoveDownMasterRule);
}
